import React from 'react';
import { Icon, IconName } from './Icon';

// Icon size variants
export type IconSize = 'sm' | 'md' | 'lg' | 'xl';

// Button variants
export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

interface IconButtonProps {
  icon: IconName;
  size?: IconSize;
  variant?: ButtonVariant;
  disabled?: boolean;
  isActive?: boolean;
  tooltip?: string;
  onClick?: () => void;
  className?: string;
  style?: React.CSSProperties;
  'aria-label'?: string;
}

// Size mappings
const sizeStyles = {
  sm: { padding: '6px', iconSize: '16px' },
  md: { padding: '8px', iconSize: '20px' },
  lg: { padding: '10px', iconSize: '24px' },
  xl: { padding: '12px', iconSize: '32px' }
};

// Variant styles
const variantStyles = {
  primary: {
    background: 'linear-gradient(180deg, #08B8D3 10%, #045F6D 100%)',
    color: '#ffffff',
    border: 'none',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  },
  secondary: {
    background: '#ffffff',
    color: '#6b7280',
    border: '1px solid #e5e7eb'
  },
  ghost: {
    background: 'transparent',
    color: '#6b7280',
    border: 'none'
  },
  danger: {
    background: '#fef2f2',
    color: '#dc2626',
    border: '1px solid #fecaca'
  }
};

export const IconButton: React.FC<IconButtonProps> = ({
  icon,
  size = 'md',
  variant = 'ghost',
  disabled = false,
  isActive = false,
  tooltip,
  onClick,
  className = '',
  style,
  'aria-label': ariaLabel,
}) => {
  const sizeStyle = sizeStyles[size];
  const variantStyle = variantStyles[variant];
  
  const baseStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '8px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s ease',
    fontFamily: 'inherit',
    opacity: disabled ? 0.5 : 1,
    transform: isActive ? 'scale(0.95)' : 'scale(1)',
    ...sizeStyle,
    ...variantStyle,
    ...style
  };

  const iconStyle: React.CSSProperties = {
    width: sizeStyle.iconSize,
    height: sizeStyle.iconSize
  };

  const handleClick = () => {
    if (!disabled && onClick) {
      onClick();
    }
  };

  return (
    <button
      type="button"
      style={baseStyle}
      onClick={handleClick}
      disabled={disabled}
      aria-label={ariaLabel || `${icon} button`}
      title={tooltip}
      className={className}
      onMouseEnter={(e) => {
        if (!disabled) {
          e.currentTarget.style.transform = 'scale(1.05)';
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled) {
          e.currentTarget.style.transform = isActive ? 'scale(0.95)' : 'scale(1)';
        }
      }}
    >
      <Icon name={icon} className="" style={iconStyle} />
    </button>
  );
};

export default IconButton;